import { useQueryClient } from '@tanstack/react-query';
import { css } from 'aphrodite';
import * as React from 'react';
import { useHistory } from 'react-router-dom';
import * as Api from '@ViewModels';
import { IAIAgentTemplate } from '../../../../extViewmodels/sdk/models/AI';
import { MastheadPortalId } from '../../../../models/AdminModels';
import { useAdminUserSession } from '../../../../models/hooks/adminStateHooks';
import {
	useAIAgentTemplatesQuery,
	useDeleteAIAgentTemplateMutation,
	useSetAIAgentTemplateActiveMutation,
} from '../../../../queries/AIAgentTemplate';
import {
	Dropdown,
	DropdownContent,
	DropdownEllipsisTrigger,
	DropdownItem,
	DropdownSelectTrigger,
} from '../../../../web/components-v2/Dropdown';
import { Button } from '../../../../web/components-v2/Button';
import {
	Dialog,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogPopup,
	DialogTitle,
} from '../../../../web/components-v2/Dialog';
import { toast } from 'sonner';
import { useErrorMessages } from '../../../../models/hooks/appStateHooks';
import { Databoard, DataboardBody } from '../../../../web/components/Databoard';
import { Badge } from '../../../../web/components/dataBoards/Badge';
import {
	DataGrid,
	DataGridColumn,
	DataGridColumns,
	DataGridRow,
	DataGridRowItem,
	DataGridRows,
} from '../../../../web/components/DataGrid';
import { LoadingSpinner } from '../../../../web/components/LoadingSpinner';
import { NavigationBreadcrumbsBar } from '../../../../web/components/NavigationBreadcrumbsBar';
import { Portal } from '../../../../web/components/Portal';
import { TextInput } from '../../../../web/components/TextInput';
import { AIAssistantDialog } from '../../../../web/components-v2/ai/AIAssistantDialog';
import { conversationTypePermissions, getFilteredConversationTypes } from '../constants';
import {
	formatConversationType,
	formatDate,
	getAssistantNameFromTemplateName,
	groupAIAgentTemplatesByHash,
} from '../utils';
import { styles } from './styles';

// Sales-related conversation types that should be hidden from admin (managed in AIDA)
const SALES_CONVERSATION_TYPES = [
	Api.AIConversationType.SalesLiveCoaching,
	Api.AIConversationType.SalesPostCallCoaching,
	Api.AIConversationType.SalesPreCallCoaching,
	Api.AIConversationType.SalesPracticeCall,
];

export function AIAgentTemplatesList() {
	const history = useHistory();
	const queryClient = useQueryClient();
	const userSession = useAdminUserSession();
	const errorMessages = useErrorMessages();
	const [searchTerm, setSearchTerm] = React.useState('');
	const [selectedConversationType, setSelectedConversationType] = React.useState('all');
	const [selectedStatus, setSelectedStatus] = React.useState('all');
	const [templateToDelete, setTemplateToDelete] = React.useState<IAIAgentTemplate | null>(null);
	const [isDeleting, setIsDeleting] = React.useState(false);
	const [templateToTest, setTemplateToTest] = React.useState<IAIAgentTemplate | null>(null);

	const { data: aiAgentTemplates, isPending, error } = useAIAgentTemplatesQuery();
	const setActiveMutation = useSetAIAgentTemplateActiveMutation();
	const deleteMutation = useDeleteAIAgentTemplateMutation({
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['aiAgentTemplatesQuery'] });
			toast.success('AI Agent deleted successfully');
			setTemplateToDelete(null);
			setIsDeleting(false);
		},
		onError: deleteError => {
			errorMessages.pushApiError(deleteError);
			setIsDeleting(false);
		},
	});

	// Get filtered conversation types based on user permissions
	const availableConversationTypes = React.useMemo(() => {
		return getFilteredConversationTypes(userSession.permissions || {});
	}, [userSession.permissions]);

	const filteredTemplates = React.useMemo(() => {
		if (!aiAgentTemplates) return [];

		return aiAgentTemplates.filter(template => {
			// Hide Sales conversation types (managed in AIDA)
			if (
				template.conversationType &&
				SALES_CONVERSATION_TYPES.includes(template.conversationType as Api.AIConversationType)
			) {
				return false;
			}

			// Check if user has permission to view this template based on its conversation type
			if (template.conversationType && template.conversationType in conversationTypePermissions) {
				const requiredPermission =
					conversationTypePermissions[template.conversationType as keyof typeof conversationTypePermissions];
				if (requiredPermission && !userSession.permissions?.[requiredPermission]) {
					return false;
				}
			}

			const matchesSearch =
				template.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
				template.description?.toLowerCase().includes(searchTerm.toLowerCase());

			const matchesConversationType =
				selectedConversationType === 'all' || template.conversationType === selectedConversationType;

			const matchesStatus =
				selectedStatus === 'all' ||
				(selectedStatus === 'active' && template.isActive) ||
				(selectedStatus === 'inactive' && !template.isActive);

			return matchesSearch && matchesConversationType && matchesStatus;
		});
	}, [aiAgentTemplates, searchTerm, selectedConversationType, selectedStatus, userSession.permissions]);

	const groupedTemplates = React.useMemo(() => {
		const groups = groupAIAgentTemplatesByHash(filteredTemplates);

		// Sort templates within each group so active templates appear first
		return groups.map(group => ({
			...group,
			templates: group.templates.sort((a, b) => {
				// Active templates first
				if (a.isActive && !b.isActive) return -1;
				if (!a.isActive && b.isActive) return 1;
				// Then sort by name
				return a.name.localeCompare(b.name);
			}),
		}));
	}, [filteredTemplates]);

	const handleTemplateClick = (id: string) => {
		history.push(`/ai-agent-templates/${id}`);
	};

	const handleCloneTemplate = (template: IAIAgentTemplate) => {
		// Navigate to detail page with clone mode
		history.push(`/ai-agent-templates/${template.id}?mode=clone`);
	};

	const handleCreateNew = () => {
		history.push('/ai-agent-templates/new');
	};

	const handleToggleActive = async (template: IAIAgentTemplate) => {
		try {
			await setActiveMutation.mutateAsync({
				id: template.id,
				isActive: !template.isActive,
			});
			queryClient.invalidateQueries({ queryKey: ['aiAgentTemplatesQuery'] });
		} catch (queryError) {
			console.error('Error toggling template active state:', queryError);
		}
	};

	const handleDeleteClick = (template: IAIAgentTemplate) => {
		setTemplateToDelete(template);
	};

	const handleDeleteConfirm = async () => {
		if (!templateToDelete) return;
		setIsDeleting(true);
		await deleteMutation.mutateAsync({ id: templateToDelete.id });
	};

	const handleDeleteCancel = () => {
		setTemplateToDelete(null);
	};

	const renderTemplateActions = (template: IAIAgentTemplate) => {
		// Find the group this template belongs to
		const group = groupedTemplates.find(g => g.templates.some(t => t.id === template.id));
		const activeTemplatesCount = group?.templates.filter(t => t.isActive).length || 0;

		// Don't show "Deactivate" if this is the only active template in the group
		const canDeactivate = template.isActive && activeTemplatesCount > 1;

		return (
			<Dropdown>
				<DropdownEllipsisTrigger />
				<DropdownContent>
					<DropdownItem onClick={() => handleTemplateClick(template.id)}>Edit</DropdownItem>
					<DropdownItem onClick={() => setTemplateToTest(template)}>Test</DropdownItem>
					<DropdownItem onClick={() => handleCloneTemplate(template)}>Clone</DropdownItem>
					{!template.isActive && <DropdownItem onClick={() => handleToggleActive(template)}>Activate</DropdownItem>}
					{canDeactivate && <DropdownItem onClick={() => handleToggleActive(template)}>Deactivate</DropdownItem>}
					{!template.isActive && <DropdownItem onClick={() => handleDeleteClick(template)}>Delete</DropdownItem>}
				</DropdownContent>
			</Dropdown>
		);
	};

	const renderContent = () => {
		if (isPending) {
			return <LoadingSpinner />;
		}

		if (error) {
			return (
				<div className={css(styles.emptyState)}>
					<p>Error loading AI agents. Please try again.</p>
				</div>
			);
		}

		if (!aiAgentTemplates || aiAgentTemplates.length === 0) {
			return (
				<div className={css(styles.emptyState)}>
					<p>No AI agents found.</p>
				</div>
			);
		}

		if (groupedTemplates.length === 0) {
			return (
				<div className={css(styles.emptyState)}>
					<p>No AI agents match your search criteria.</p>
				</div>
			);
		}

		return (
			<div>
				{groupedTemplates.map(group => {
					const verticalSelector = group.selectors?.Vertical;

					return (
						<div key={group.hash} className={css(styles.groupContainer)}>
							{/* Group Header */}
							<div className={css(styles.groupHeader)}>
								<h2 className={css(styles.groupTitle)}>{verticalSelector || 'All'}</h2>
								<span className={css(styles.groupMeta)}>
									{group.templates.length} variant{group.templates.length !== 1 ? 's' : ''}
								</span>
							</div>

							{/* Separate Databoard for each group */}
							<Databoard>
								<DataboardBody>
									<DataGrid>
										<DataGridColumns>
											<DataGridColumn sticky>Name</DataGridColumn>
											<DataGridColumn sticky>Type</DataGridColumn>
											<DataGridColumn sticky>Vertical</DataGridColumn>
											<DataGridColumn sticky>Status</DataGridColumn>
											<DataGridColumn sticky>Attachments</DataGridColumn>
											<DataGridColumn sticky>Created</DataGridColumn>
											<DataGridColumn sticky>Actions</DataGridColumn>
										</DataGridColumns>
										<DataGridRows>
											{group.templates.map(template => (
												<DataGridRow key={template.id} styles={[styles.templateRow]}>
													<DataGridRowItem>
														<div onClick={() => handleTemplateClick(template.id)} style={{ cursor: 'pointer' }}>
															<div className={css(styles.templateName)}>{template.name}</div>
															{template.description && (
																<div className={css(styles.templateDescription)}>{template.description}</div>
															)}
														</div>
													</DataGridRowItem>
													<DataGridRowItem>
														<div onClick={() => handleTemplateClick(template.id)} style={{ cursor: 'pointer' }}>
															{template.conversationType ? formatConversationType(template.conversationType) : 'N/A'}
														</div>
													</DataGridRowItem>
													<DataGridRowItem>
														<div onClick={() => handleTemplateClick(template.id)} style={{ cursor: 'pointer' }}>
															{template.selectors?.Vertical || '—'}
														</div>
													</DataGridRowItem>
													<DataGridRowItem>
														<div onClick={() => handleTemplateClick(template.id)} style={{ cursor: 'pointer' }}>
															<Badge
																text={template.isActive ? 'Active' : 'Inactive'}
																foregroundColor={template.isActive ? '#28a745' : '#6c757d'}
															/>
														</div>
													</DataGridRowItem>
													<DataGridRowItem>
														<div onClick={() => handleTemplateClick(template.id)} style={{ cursor: 'pointer' }}>
															<div className={css(styles.attachmentCount)}>
																{template.attachments?.length || 0} file{template.attachments?.length !== 1 ? 's' : ''}
															</div>
														</div>
													</DataGridRowItem>
													<DataGridRowItem>
														<div onClick={() => handleTemplateClick(template.id)} style={{ cursor: 'pointer' }}>
															{template.creationDate ? formatDate(template.creationDate) : 'N/A'}
														</div>
													</DataGridRowItem>
													<DataGridRowItem>
														<div onClick={(e: React.MouseEvent) => e.stopPropagation()}>
															{renderTemplateActions(template)}
														</div>
													</DataGridRowItem>
												</DataGridRow>
											))}
										</DataGridRows>
									</DataGrid>
								</DataboardBody>
							</Databoard>
						</div>
					);
				})}
			</div>
		);
	};

	const pathComponentNameProvider = (pathComponent: string) => {
		// For "ai-agent-templates", show a more user-friendly name
		if (pathComponent === 'ai-agent-templates') {
			return 'AI Agents';
		}
		// Return undefined to use the default behavior
		return undefined;
	};

	return (
		<div className={css(styles.container)}>
			<Portal destination={MastheadPortalId}>
				<NavigationBreadcrumbsBar pathComponentNameProvider={pathComponentNameProvider} />
			</Portal>

			<div className={css(styles.filtersContainer)}>
				<div className={css(styles.filters)}>
					<TextInput
						type='text'
						className={css(styles.searchInput)}
						placeholder='Search templates...'
						value={searchTerm}
						onChange={e => setSearchTerm(e.target.value)}
					/>
					<Dropdown>
						<DropdownSelectTrigger styles={[styles.conversationTypeFilter]}>
							{selectedConversationType === 'all'
								? 'All Conversation Types'
								: availableConversationTypes.find(type => type.value === selectedConversationType)?.label ||
									'All Conversation Types'}
						</DropdownSelectTrigger>
						<DropdownContent>
							<DropdownItem onClick={() => setSelectedConversationType('all')}>All Conversation Types</DropdownItem>
							{availableConversationTypes.map(type => (
								<DropdownItem key={type.value} onClick={() => setSelectedConversationType(type.value)}>
									{type.label}
								</DropdownItem>
							))}
						</DropdownContent>
					</Dropdown>
					<Dropdown>
						<DropdownSelectTrigger styles={[styles.statusFilter]}>
							{selectedStatus === 'all' ? 'All Status' : selectedStatus === 'active' ? 'Active Only' : 'Inactive Only'}
						</DropdownSelectTrigger>
						<DropdownContent>
							<DropdownItem onClick={() => setSelectedStatus('all')}>All Status</DropdownItem>
							<DropdownItem onClick={() => setSelectedStatus('active')}>Active Only</DropdownItem>
							<DropdownItem onClick={() => setSelectedStatus('inactive')}>Inactive Only</DropdownItem>
						</DropdownContent>
					</Dropdown>
				</div>
				<Button onClick={handleCreateNew} variant='primary'>
					Create New AI Agent
				</Button>
			</div>

			{renderContent()}

			{/* Delete Confirmation Modal */}
			<Dialog open={!!templateToDelete} onOpenChange={open => !open && handleDeleteCancel()}>
				<DialogPopup>
					<DialogHeader>
						<DialogTitle>Delete AI Agent?</DialogTitle>
						<DialogDescription>
							Are you sure you want to delete &quot;{templateToDelete?.name}&quot;? This action cannot be undone.
						</DialogDescription>
					</DialogHeader>
					<DialogFooter>
						<Button onClick={handleDeleteCancel} variant='secondary' disabled={isDeleting}>
							Cancel
						</Button>
						<Button onClick={handleDeleteConfirm} variant='destructive-secondary' disabled={isDeleting}>
							{isDeleting ? 'Deleting...' : 'Delete'}
						</Button>
					</DialogFooter>
				</DialogPopup>
			</Dialog>

			{/* Test Chat Modal */}
			{templateToTest && (
				<AIAssistantDialog
					open={!!templateToTest}
					onOpenChange={open => !open && setTemplateToTest(null)}
					assistantName={getAssistantNameFromTemplateName(templateToTest.name)}
					conversationType={
						(templateToTest.conversationType as Api.AIConversationType) || Api.AIConversationType.SiteWide
					}
					agentTemplateId={templateToTest.id}
					title={`Test: ${templateToTest.name}`}
					initialMessage={`Hello! I'm the ${templateToTest.name} agent. How can I help you today?`}
					placeholder='Send a test message...'
				/>
			)}
		</div>
	);
}
